import { bar, divider, icon, panel, pips, statRow } from "../kit/components.ts";
import { h } from "../kit/dom.ts";
import type { SheetSkill, SheetView } from "./sheet-model.ts";
import { keyHint } from "./settings.ts";

/**
 * Ficha da classe (F82, tecla C, remapeável; mockup `eldrun-ui-e.png` adaptado): perfil, retrato pela forma
 * da classe, atributos e vitais, a passiva, a ativa e a ultimate, as estatísticas da partida e a fantasia
 * da classe. O conteúdo redesenha só quando a projeção muda; o topo (com o ✕) fica. C ou Esc fecha.
 */
export class SheetPanel {
  readonly element: HTMLElement;
  private readonly body: HTMLElement;
  private key = "";

  constructor(parent: HTMLElement) {
    this.body = h("div", { class: "ficha-conteudo" });
    const top = panel({ title: "Ficha", icon: "icone-ficha", onClose: () => this.toggle(false), closeLabel: "Fechar a ficha", className: "k-painel--sub ficha-barra" }, keyHint("sheet", "{} ou Esc fecha"));
    this.element = h("section", { id: "ficha", class: "ficha-painel", "aria-label": "Ficha da classe" }, top, this.body);
    this.element.hidden = true;
    this.element.addEventListener("keydown", (e) => {
      if (e.key === "Escape") this.toggle(false);
    });
    parent.appendChild(this.element);
  }

  get open(): boolean {
    return !this.element.hidden;
  }

  toggle(open = !this.open): void {
    this.element.hidden = !open;
    if (open) {
      this.element.tabIndex = -1;
      this.element.focus?.();
    }
  }

  update(view: SheetView): void {
    const key = JSON.stringify(view);
    if (key === this.key) return;
    this.key = key;
    this.body.replaceChildren(
      h("div", { class: "ficha-linha ficha-linha--1" }, this.profile(view), this.portrait(view), h("div", { class: "ficha-coluna" }, this.attributes(view), this.vitals(view))),
      h("div", { class: "ficha-linha ficha-linha--2" }, this.match(view), this.skills(view), this.biography(view)),
    );
  }

  private profile(v: SheetView): HTMLElement {
    const swatch = h("span", { class: "ficha-cor", "aria-hidden": "true" });
    swatch.style.setProperty("--cor", v.color);
    const icons = { Classe: "icone-classe", Papel: "icone-perfil", Jogador: "icone-nivel", Afinidade: "icone-mira", Juramentos: "icone-talentos" } as const;
    return panel(
      { title: "Perfil", icon: "icone-perfil", className: "k-painel--sub ficha-perfil" },
      h("h3", { class: "ficha-nome" }, v.name),
      h("span", { class: "ficha-titulo" }, `${v.className} / ${v.role}`),
      divider(),
      ...v.profile.map(([label, value]) =>
        statRow(icons[label as keyof typeof icons], label, label === "Jogador" ? h("span", { class: "ficha-selo" }, swatch, `${value} · ${v.color.toUpperCase()}`) : value),
      ),
    );
  }

  /** Retrato: o ícone da classe pela forma, grande, no acento do slot, e o nome da classe. */
  private portrait(v: SheetView): HTMLElement {
    return h(
      "div",
      { class: "ficha-retrato", role: "img", "aria-label": `${v.className}, ${v.role}` },
      h("span", { class: "ficha-retrato-glifo", "aria-hidden": "true" }, v.glyph),
      h("span", { class: "ficha-retrato-nome", "aria-hidden": "true" }, v.className),
    );
  }

  private attributes(v: SheetView): HTMLElement {
    return panel(
      { title: "Atributos", icon: "icone-atributos", className: "k-painel--sub ficha-atributos" },
      h(
        "div",
        { class: "ficha-quadros" },
        ...v.tiles.map((t) =>
          h("div", { class: "ficha-quadro", "data-atributo": t.id, title: `${t.label}: ${t.value} ${t.hint}` }, icon(t.icon), h("span", { class: "ficha-quadro-nome" }, t.label), h("span", { class: "ficha-quadro-valor" }, t.value), h("span", { class: "ficha-quadro-dica" }, t.hint)),
        ),
      ),
    );
  }

  private vitals(v: SheetView): HTMLElement {
    return panel(
      { title: "Vitais", icon: "vital-vida", className: "k-painel--sub ficha-vitais" },
      ...v.bars.map((b) => {
        const el = bar({ label: b.label, value: b.value, max: b.max, text: b.text, ...(b.icon ? { icon: b.icon } : {}) });
        el.dataset["vital"] = b.id;
        return el;
      }),
    );
  }

  private match(v: SheetView): HTMLElement {
    return panel(
      { title: "Partida", icon: "icone-nivel", className: "k-painel--sub ficha-partida" },
      v.stats ? h("div", { class: "ficha-estatisticas" }, ...v.stats.map(([label, value]) => statRow(undefined, label, value))) : h("p", { class: "hint" }, "Sem estatísticas nesta partida."),
    );
  }

  private skills(v: SheetView): HTMLElement {
    const card = (s: SheetSkill) =>
      h(
        "div",
        { class: "ficha-habilidade", "data-skill": s.id },
        h("span", { class: "ficha-habilidade-moldura", "aria-hidden": "true" }, v.glyph),
        h(
          "div",
          { class: "ficha-habilidade-texto" },
          h("span", { class: "ficha-habilidade-nome" }, s.name),
          h("span", { class: "ficha-habilidade-tipo" }, `${s.kind}${s.key ? ` · ${s.key}` : ""} · ${s.meta}`),
          h("span", { class: "ficha-habilidade-desc" }, s.description),
        ),
        s.levels > 0 ? pips(s.level, s.levels) : h("span", {}),
      );
    return panel({ title: "Habilidades", icon: "icone-talentos", className: "k-painel--sub ficha-habilidades" }, ...v.skills.map(card));
  }

  private biography(v: SheetView): HTMLElement {
    return panel({ title: "Biografia", icon: "icone-biografia", className: "k-painel--sub ficha-biografia" }, h("div", { class: "ficha-bio" }, ...v.biography.map((p) => h("p", {}, p))));
  }

  remove(): void {
    this.element.remove();
  }
}
